import {
  FirebaseStorage,
  ref,
  UploadTask,
  getDownloadURL,
  getMetadata,
  getStorage,
  uploadBytesResumable,
  uploadBytes,
} from 'firebase/storage';
import React from 'react';
// Unsure why this is needed, but throws an error without it
// SEE https://github.com/babel/babel/issues/9849
import 'regenerator-runtime/runtime';
import { Site } from '../buildfire/rdb/cases';
import useFirebase from './useFirebase';

// ===== Cache Infrastructure =====

interface URLCacheEntry {
  url: string;
  contentType: string;
  expiresAt: number;
  caseTimestamp: string;
}

interface BlobCacheEntry {
  blob: Blob;
  contentType: string;
  caseTimestamp: string;
  lastAccessed: number;
}

interface FileCacheEntry {
  file: File;
  caseTimestamp: string;
  lastAccessed: number;
}

const urlCache = new Map<string, URLCacheEntry>();
const blobCache = new Map<string, BlobCacheEntry>();
const fileCache = new Map<string, FileCacheEntry>();
const missingThumbnailCache = new Set<string>();

const MAX_CACHED_CASES = 5;
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
const THUMBNAIL_PREFIX = 'thumb_';

const getCacheKey = (uid: string, filename: string) => `${uid}/${filename}`;

const getCaseTimestamp = (filename: string): string => {
  const match = filename.match(/case-attachment-(\d+)-/);
  return match ? match[1] : 'unknown';
};

export interface UploadSitesResult {
  sites: Site[];
  hasFailures: boolean;
  failedCount: number;
  successCount: number;
}

export const uploadSites = async (
  storage: FirebaseStorage,
  sites: Site[],
  userId: string,
  uploadCallback?: (
    num: number,
    total: number,
    name: string,
    siteIndex: number
  ) => void
): Promise<UploadSitesResult> => {
  let totalFailures = 0;
  let totalSuccesses = 0;

  const uploadedSites = await Promise.all(
    sites.map(async (site, siteIndex) => {
      const filesToUpload = site.images
        .map((img) => {
          if (img instanceof File) return img;
          // Extract failed files for retry
          if (
            typeof img === 'object' &&
            'status' in img &&
            img.status === 'failed' &&
            img.originalFile
          ) {
            return img.originalFile;
          }
          return null;
        })
        .filter((img): img is File => img !== null);

      if (filesToUpload.length === 0) {
        return {
          ...site,
          additionalInfo: site.additionalInfo || '',
        };
      }

      const uploadResults = await uploadImagesInBatches(
        storage,
        3,
        filesToUpload,
        userId,
        (num, total, name) => uploadCallback?.(num, total, name, siteIndex)
      );

      let uploadIndex = 0;
      const updatedImages = site.images.map((img) => {
        // Handle File objects
        if (img instanceof File) {
          const result = uploadResults[uploadIndex++];
          if (result.success) {
            totalSuccesses++;
            return result.filename;
          } else {
            totalFailures++;
            return {
              filename: result.filename,
              status: 'failed' as const,
              error: result.error,
              originalFile: img, // Preserve original File for retry
            };
          }
        }

        // Handle retry of failed ImageStatus objects
        if (
          typeof img === 'object' &&
          'status' in img &&
          img.status === 'failed' &&
          img.originalFile
        ) {
          const result = uploadResults[uploadIndex++];
          if (result.success) {
            totalSuccesses++;
            return result.filename;
          } else {
            totalFailures++;
            return {
              filename: result.filename,
              status: 'failed' as const,
              error: result.error,
              originalFile: img.originalFile, // Preserve original File for next retry
            };
          }
        }

        // Return existing strings or uploaded ImageStatus objects as-is
        return img;
      });

      return {
        location: site.location,
        description: site.description,
        additionalInfo: site.additionalInfo || '',
        images: updatedImages,
      };
    })
  );

  return {
    sites: uploadedSites,
    hasFailures: totalFailures > 0,
    failedCount: totalFailures,
    successCount: totalSuccesses,
  };
};

const TIMEOUT_MS = 180000;

const uploadImagesInBatches = async (
  storage: FirebaseStorage,
  batchSize: number,
  files: FileList | File[],
  uid: string,
  onUploaded?: (num: number, total: number, name: string) => void
): Promise<UploadResult[]> => {
  const fileArr: File[] = [...(files as any)];
  let batch = batchSize === 0 ? 999 : batchSize;

  let results: UploadResult[] = [];
  const totalBatches = Math.ceil(fileArr.length / batch);
  let batchNum = 1;
  let uploadedCount = 0;
  for (let i = 0; i < fileArr.length; i += batch) {
    const chunk = fileArr.slice(i, i + batch);
    console.log(`Uploading batch ${batchNum}/${totalBatches}`);
    const batchResults = await uploadImages(storage, chunk, uid, (name) => {
      console.log(`Uploaded image ${uploadedCount} of ${fileArr.length}`);
      onUploaded?.(uploadedCount, fileArr.length, name);
      uploadedCount++;
    });
    console.log(`Uploaded batch ${batchNum}/${totalBatches}`);
    results = results.concat(batchResults);
    batchNum++;
  }

  return results;
};

export const uploadProfilePicture = (
  storage: FirebaseStorage,
  image: File,
  uid: string
): Promise<string> => {
  const imageRef = ref(storage, `profile_pictures/${uid}/${image.name}`);
  return new Promise<string>(async (resolve, reject) => {
    try {
      const result = await uploadBytes(imageRef, image);
      const { name } = result.ref;
      resolve(name);
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
};

export interface UploadResult {
  success: boolean;
  filename: string;
  error?: string;
}

const uploadImages = (
  storage: FirebaseStorage,
  files: FileList | File[],
  uid: string,
  onUploaded?: (name: string) => void
): Promise<UploadResult[]> => {
  const tasks: UploadTask[] = [];
  for (let i = 0; i < files.length; i++) {
    const index = i;
    const imageRef = ref(storage, `user_uploads/${uid}/${files[index].name}`);
    const task = uploadBytesResumable(imageRef, files[index]);
    tasks.push(task);
  }

  const promises = tasks.map((task, i) => {
    return new Promise<UploadResult>((resolve) => {
      const filename = files[i].name;

      let lastCalled = Date.now();
      const interval = setInterval(() => {
        if (Date.now() - lastCalled > TIMEOUT_MS) {
          task.cancel();
          clearInterval(interval);
          resolve({
            success: false,
            filename,
            error:
              'Timeout: No progress for 180 seconds. Please check your internet connection.',
          });
        }
      }, 1000);

      task.on(
        'state_changed',
        (snapshot) => {
          lastCalled = Date.now();

          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload ${snapshot.ref.name}: ${progress}%`);
        },
        (err) => {
          console.error(`ERROR uploading ${task.snapshot.ref.name}`, err);
          clearInterval(interval);
          resolve({
            success: false,
            filename,
            error: err.message || 'Upload failed',
          });
        },
        () => {
          const { name } = task.snapshot.ref;
          console.log(`Uploaded ${name} successfully`);
          onUploaded?.(name);
          clearInterval(interval);
          resolve({
            success: true,
            filename: name,
          });
        }
      );
    });
  });

  return Promise.all(promises);
};

// ===== Cache Management =====

const cleanCache = () => {
  const now = Date.now();

  // Remove expired entries from all caches
  urlCache.forEach((entry, key) => {
    if (now > entry.expiresAt) {
      urlCache.delete(key);
    }
  });

  blobCache.forEach((entry, key) => {
    if (now - entry.lastAccessed > MAX_AGE_MS) {
      blobCache.delete(key);
    }
  });

  fileCache.forEach((entry, key) => {
    if (now - entry.lastAccessed > MAX_AGE_MS) {
      fileCache.delete(key);
    }
  });

  // Case-based eviction: keep only MAX_CACHED_CASES most recent cases
  const getCaseTimestampsFromCache = (cache: Map<string, any>) => {
    const timestamps = new Map<string, number>();
    cache.forEach((entry) => {
      const ts = entry.caseTimestamp;
      const access = entry.lastAccessed || entry.expiresAt || 0;
      if (!timestamps.has(ts) || access > timestamps.get(ts)!) {
        timestamps.set(ts, access);
      }
    });
    return timestamps;
  };

  // Collect all case timestamps across caches
  const allCaseTimestamps = new Map<string, number>();
  [blobCache, fileCache].forEach((cache) => {
    const timestamps = getCaseTimestampsFromCache(cache);
    timestamps.forEach((access, ts) => {
      if (!allCaseTimestamps.has(ts) || access > allCaseTimestamps.get(ts)!) {
        allCaseTimestamps.set(ts, access);
      }
    });
  });

  // If we have more than MAX_CACHED_CASES, remove oldest ones
  if (allCaseTimestamps.size > MAX_CACHED_CASES) {
    const sortedCases = Array.from(allCaseTimestamps.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by access time, newest first
      .slice(MAX_CACHED_CASES); // Get cases to remove

    const casesToRemove = new Set(sortedCases.map(([ts]) => ts));

    [urlCache, blobCache, fileCache].forEach((cache) => {
      cache.forEach((entry: any, key: string) => {
        if (casesToRemove.has(entry.caseTimestamp)) {
          cache.delete(key);
        }
      });
    });
  }
};

const getUserUploadFileRef = (
  storage: FirebaseStorage,
  file: string,
  uid: string
) => {
  return ref(storage, `user_uploads/${uid}/${file}`);
};

const getFileDownloadUrl = (
  storage: FirebaseStorage,
  file: string,
  uid: string
): Promise<string> => {
  const fileRef = getUserUploadFileRef(storage, file, uid);
  return getDownloadURL(fileRef);
};

// Synchronous cache check for initial state optimization
export const getCachedUrlSync = (
  filename: string,
  uid: string
): { url: string; contentType: string } | null => {
  const key = getCacheKey(uid, filename);
  const cached = urlCache.get(key);

  if (cached && Date.now() < cached.expiresAt) {
    return { url: cached.url, contentType: cached.contentType };
  }
  return null;
};

// Cached version of getFileDownloadUrl with metadata
export const getCachedDownloadUrl = async (
  storage: FirebaseStorage,
  filename: string,
  uid: string
): Promise<{ url: string; contentType: string }> => {
  const key = getCacheKey(uid, filename);
  const cached = urlCache.get(key);

  if (cached && Date.now() < cached.expiresAt) {
    return { url: cached.url, contentType: cached.contentType };
  }

  const url = await getFileDownloadUrl(storage, filename, uid);

  // Get metadata once and cache it with the URL
  const ref = getUserUploadFileRef(storage, filename, uid);
  const metadata = await getMetadata(ref);
  const contentType = metadata.contentType || 'image/jpeg';

  const expiresAt = Date.now() + 50 * 60 * 1000; // 50 min (Firebase URLs valid ~1 hour)

  urlCache.set(key, {
    url,
    contentType,
    expiresAt,
    caseTimestamp: getCaseTimestamp(filename),
  });

  cleanCache();
  return { url, contentType };
};

const isObjectNotFoundError = (error: any) =>
  error?.code === 'storage/object-not-found';

const getThumbnailFilename = (filename: string) => {
  if (!filename) {
    return filename;
  }
  if (filename.startsWith(THUMBNAIL_PREFIX)) {
    return filename;
  }
  return `${THUMBNAIL_PREFIX}${filename}`;
};

const getPreviewDownloadUrl = async (
  storage: FirebaseStorage,
  filename: string,
  uid: string
): Promise<{
  url: string;
  contentType: string;
  source: 'thumbnail' | 'original';
}> => {
  const thumbnailFilename = getThumbnailFilename(filename);

  if (
    thumbnailFilename &&
    thumbnailFilename !== filename &&
    !missingThumbnailCache.has(thumbnailFilename)
  ) {
    try {
      const result = await getCachedDownloadUrl(
        storage,
        thumbnailFilename,
        uid
      );
      return { ...result, source: 'thumbnail' };
    } catch (error) {
      if (isObjectNotFoundError(error)) {
        missingThumbnailCache.add(thumbnailFilename);
      } else {
        throw error;
      }
    }
  }

  const result = await getCachedDownloadUrl(storage, filename, uid);
  return { ...result, source: 'original' };
};

// Cached blob fetch
export const getCachedBlob = async (
  storage: FirebaseStorage,
  filename: string,
  uid: string
): Promise<{ blob: Blob; contentType: string }> => {
  const key = getCacheKey(uid, filename);
  const cached = blobCache.get(key);

  if (cached) {
    cached.lastAccessed = Date.now();
    return { blob: cached.blob, contentType: cached.contentType };
  }

  const { url, contentType } = await getCachedDownloadUrl(
    storage,
    filename,
    uid
  );
  const response = await fetch(url);
  const blob = await response.blob();

  blobCache.set(key, {
    blob,
    contentType,
    caseTimestamp: getCaseTimestamp(filename),
    lastAccessed: Date.now(),
  });

  cleanCache();
  return { blob, contentType };
};

const getImageAsFile = async (
  storage: FirebaseStorage,
  file: string,
  uid: string
): Promise<File> => {
  const key = getCacheKey(uid, file);
  const cached = fileCache.get(key);

  if (cached) {
    cached.lastAccessed = Date.now();
    return cached.file;
  }

  const { blob, contentType } = await getCachedBlob(storage, file, uid);
  const fileObj = new File([blob], file, { type: contentType });

  fileCache.set(key, {
    file: fileObj,
    caseTimestamp: getCaseTimestamp(file),
    lastAccessed: Date.now(),
  });

  return fileObj;
};

const useFiles = (imageUrls: string[], uid: string) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const { app } = useFirebase();
  React.useEffect(() => {
    const storage = getStorage(app);
    Promise.all(imageUrls.map((url) => getImageAsFile(storage, url, uid))).then(
      setFiles
    );
  }, [uid, imageUrls, app]);
  return files;
};

export {
  uploadImages,
  getFileDownloadUrl,
  getPreviewDownloadUrl,
  getImageAsFile,
  useFiles,
  getUserUploadFileRef as getFileRef,
};
